import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, XCircle } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BookingSuccessPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const isDemo = searchParams.get('demo');
  const appointmentId = searchParams.get('appointment_id');
  
  const [status, setStatus] = useState('checking');
  const [appointment, setAppointment] = useState(null);

  useEffect(() => {
    if (isDemo && appointmentId) {
      handleDemoMode(appointmentId);
    } else if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [sessionId, isDemo, appointmentId]);

  const handleDemoMode = async (appointmentId) => {
    try {
      const apptResponse = await axios.get(`${API}/appointments/${appointmentId}`);
      setAppointment(apptResponse.data);
      setStatus('success');
    } catch (error) {
      console.error('Error fetching appointment:', error);
      setStatus('expired');
    }
  };

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setStatus('timeout');
      return;
    }

    try {
      const response = await axios.get(`${API}/checkout/status/${sessionId}`);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setStatus('success');
        if (data.metadata?.appointment_id) {
          const apptResponse = await axios.get(`${API}/appointments/${data.metadata.appointment_id}`);
          setAppointment(apptResponse.data);
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('expired');
        return;
      }

      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center px-6">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="max-w-md w-full bg-background-paper rounded-md border border-border p-8 text-center"
      >
        {status === 'checking' && (
          <div data-testid="payment-checking-status">
            <Clock size={64} weight="light" className="text-accent mx-auto mb-4 animate-pulse" />
            <h2 className="text-2xl font-serif font-medium mb-2">Processing Payment...</h2>
            <p className="text-foreground-secondary">Please wait while we confirm your booking</p>
          </div>
        )}

        {status === 'success' && (
          <div data-testid="payment-success-status">
            <CheckCircle size={64} weight="fill" className="text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-medium mb-2">Booking Confirmed!</h2>
            <p className="text-foreground-secondary mb-6">
              {isDemo 
                ? 'Demo booking created successfully. In production, payment would be processed via Stripe.'
                : 'Your appointment has been successfully booked and payment confirmed.'}
            </p>
            {appointment && (
              <div className="bg-background-subtle rounded-md p-6 mb-6 text-left">
                <h3 className="font-medium mb-3">Appointment Details</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Service:</span>
                    <span className="font-medium">{appointment.service_name}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Date:</span>
                    <span className="font-medium">{appointment.appointment_date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">Time:</span>
                    <span className="font-medium">{appointment.appointment_time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-secondary">{isDemo ? 'Amount:' : 'Amount Paid:'}</span>
                    <span className="font-medium">${appointment.amount.toFixed(2)}</span>
                  </div>
                  {isDemo && (
                    <div className="mt-4 pt-4 border-t border-border">
                      <div className="bg-accent-soft p-3 rounded text-xs text-foreground-secondary">
                        💡 Demo Mode: Payment was bypassed for testing. In production, Stripe will process real payments.
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
            <p className="text-sm text-foreground-secondary mb-6">
              {isDemo 
                ? 'You can view this booking in the admin dashboard.'
                : 'A confirmation has been sent to your email.'}
            </p>
            <Button
              data-testid="back-to-home-success-btn"
              onClick={() => navigate('/demos/beauty-salon')}
              className="bg-primary text-primary-foreground hover:bg-primary/90 w-full"
            >
              Back to Home
            </Button>
          </div>
        )}

        {(status === 'expired' || status === 'timeout') && (
          <div data-testid="payment-failed-status">
            <XCircle size={64} weight="fill" className="text-destructive mx-auto mb-4" />
            <h2 className="text-2xl font-serif font-medium mb-2">Payment {status === 'expired' ? 'Expired' : 'Timeout'}</h2>
            <p className="text-foreground-secondary mb-6">
              {status === 'expired' 
                ? 'Your payment session has expired. Please try booking again.'
                : 'We could not verify your payment. Please check your email or contact us.'}
            </p>
            <div className="flex gap-3">
              <Button
                data-testid="try-again-btn"
                onClick={() => navigate('/demos/beauty-salon/booking')}
                variant="outline"
                className="flex-1"
              >
                Try Again
              </Button>
              <Button
                data-testid="back-to-home-error-btn"
                onClick={() => navigate('/demos/beauty-salon')}
                className="flex-1"
              >
                Back to Home
              </Button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
};

export default BookingSuccessPage;